import { useCallback, useRef, useState } from 'react';

import { ColumnOrderState, Updater } from '@tanstack/react-table';

import { getFromLocalStorage, setToLocalStorage } from '@/utils/localStorage';

const COLUMN_ORDER_STORAGE_PREFIX = 'table-column-order';

/** Builds the localStorage key for a table's column order, or null when persistence is off. */
const buildStorageKey = (tableId: string | undefined): string | null =>
  tableId ? `${COLUMN_ORDER_STORAGE_PREFIX}-${tableId}` : null;

interface UseColumnOrderOptions {
  /** Fallback order used when nothing is persisted yet (or persistence is off). */
  initialOrder?: ColumnOrderState;
}

interface UseColumnOrderResult {
  columnOrder: ColumnOrderState;
  onColumnOrderChange: (updaterOrValue: Updater<ColumnOrderState>) => void;
  /** Persists the current order on demand (a no-op when `tableId` is undefined). */
  saveColumnOrder: () => void;
}

/**
 * Manages column order state with localStorage persistence, auto-saving on every change.
 *
 * The single source of truth for column-order persistence, consumed both by `useDataTable`
 * (the standard table path) and directly by tables that own their own TanStack instance
 * (e.g. the time-tracking approvals table). The storage key — `table-column-order-{tableId}`
 * — is shared, so a table can move onto/off `useDataTable` later without resetting a user's
 * saved preference. Mirrors {@link useColumnVisibility}.
 *
 * Pass `tableId` = undefined to disable persistence (state stays in memory only).
 */
export function useColumnOrder(
  tableId: string | undefined,
  { initialOrder }: UseColumnOrderOptions = {}
): UseColumnOrderResult {
  const storageKey = buildStorageKey(tableId);

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    if (!storageKey) return initialOrder ?? [];
    return getFromLocalStorage<ColumnOrderState>(storageKey) ?? initialOrder ?? [];
  });

  const columnOrderRef = useRef(columnOrder);
  columnOrderRef.current = columnOrder;

  const onColumnOrderChange = useCallback(
    (updaterOrValue: Updater<ColumnOrderState>) => {
      const newOrder =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnOrderRef.current)
          : updaterOrValue;

      setColumnOrder(newOrder);
      columnOrderRef.current = newOrder;
      if (storageKey) setToLocalStorage(storageKey, newOrder);
    },
    [storageKey]
  );

  const saveColumnOrder = useCallback(() => {
    if (storageKey) setToLocalStorage(storageKey, columnOrderRef.current);
  }, [storageKey]);

  return { columnOrder, onColumnOrderChange, saveColumnOrder };
}
